import "./ApiKeyList.scss";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useEffect, useReducer, useState } from "react";
import {
  AddOrUpdateApiKey,
  GetApiKeyList,
  GetAppKeys,
} from "../../../utils/apiCalls/apiKeys";
import {
  BackArrow,
  EditIcons,
  PlusIcon,
} from "../../../assets/icons-components/Icons";
import { onKeyActivate } from "../../../utils/a11y";

import { IconButton } from "@mui/material";
import SelectInput from "../../../components/SelectInput/SelectInput";
import Buttons from "../../../components/Buttons/Buttons";
import FormModal from "../../../components/FormModal/FormModal";
import DateInput from "../../../components/DateInput/DateInput";
import { useLocation, useNavigate } from "react-router-dom";
import CustomTooltip from "../../../components/CustomTooltip/CustomTooltip";
import dayjs from "dayjs";
import Spinner from "../../../components/Spinner/Spinner";
import TableStatusRow from "../../../components/TableStatusRow/TableStatusRow";
import { useToast } from "../../../utils/ToastContext";

const API_KEY_TABLE_COLUMN_COUNT = 5;

const initialKeyFormState = {
  referenceModal: false,
  isEditMode: false,
  selectedAppType: null,
  selectedAppTypeName: "",
  expiryDate: "",
  appTypeError: "",
  expiryDateError: "",
};

function keyFormReducer(state, action) {
  switch (action.type) {
    case "OPEN_GENERATE":
      return { ...initialKeyFormState, referenceModal: true };
    case "OPEN_EDIT":
      return {
        ...initialKeyFormState,
        referenceModal: true,
        isEditMode: true,
        selectedAppType: action.appType,
        selectedAppTypeName: action.appTypeName,
        expiryDate: action.expiryDate,
      };
    case "CLOSE":
      return { ...state, referenceModal: false };
    case "SET_APP_TYPE":
      return { ...state, selectedAppType: action.value };
    case "SET_EXPIRY_DATE":
      return { ...state, expiryDate: action.value, expiryDateError: "" };
    case "SET_VALIDATION_ERRORS":
      return {
        ...state,
        appTypeError: action.appTypeError,
        expiryDateError: action.expiryDateError,
      };
    default:
      return state;
  }
}

const ApiKeyList = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [apiKeyList, setApiKeyList] = useState([]);
  const [keyForm, dispatchKeyForm] = useReducer(keyFormReducer, initialKeyFormState);
  const { showToast } = useToast();
  const [appKeysData, setAppKeysData] = useState([]);
  const [isKeysLoading, setIsKeysLoading] = useState(true);
  const [keysFetchError, setKeysFetchError] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const data = location?.state?.rowData;

  const handleGenerateKey = () => {
    dispatchKeyForm({ type: "OPEN_GENERATE" });
    getAllApiKeyList();
  };

  const getAllApiKeyList = async () => {
    try {
      const res = await GetApiKeyList(data?.id);
      const { data: apiKeyData, statusCode } = res;
      if (statusCode === 200) {
        const formattedData = apiKeyData.map((item) => ({
          label: item.appType,
          value: item.id,
        }));
        setApiKeyList(formattedData);
      }
    } catch (error) {
      console.error("Error fetching API key list:", error?.message);
    }
  };

  const getAppKeys = async () => {
    setIsKeysLoading(true);
    setKeysFetchError("");
    try {
      const res = await GetAppKeys(data?.id);
      const { data: appKeys, statusCode, message } = res;
      if (statusCode === 200) {
        setAppKeysData(appKeys);
      } else if (statusCode === 204) {
        setAppKeysData([]);
      } else {
        setAppKeysData([]);
        setKeysFetchError(message || "Failed to load API keys");
      }
    } catch (error) {
      setAppKeysData([]);
      setKeysFetchError(error?.message || "Failed to load API keys");
    }
    setIsKeysLoading(false);
  };

  useEffect(() => {
    getAppKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hanldeAddApiKey = async () => {
    const nextExpiryDateError = keyForm.expiryDate ? "" : "This field is required";
    dispatchKeyForm({
      type: "SET_VALIDATION_ERRORS",
      appTypeError: "",
      expiryDateError: nextExpiryDateError,
    });
    if (nextExpiryDateError) return;

    setIsLoading(true);
    const payload = {
      appType: keyForm.selectedAppType,
      expiryDate: keyForm.expiryDate
        ? dayjs(keyForm.expiryDate, "DD-MM-YYYY").toISOString()
        : null,
      healthProfessionalId: data?.id || 0,
    };

    try {
      const res = await AddOrUpdateApiKey(payload);
      const { message } = res;
      setIsLoading(false);
      dispatchKeyForm({ type: "CLOSE" });
      showToast(message, "success");
      getAppKeys();
    } catch (error) {
      setIsLoading(false);
      dispatchKeyForm({ type: "CLOSE" });
      showToast(error?.message, "error");
      getAppKeys();
    }
  };

  const handleAppKeyEdit = (key) => {
    dispatchKeyForm({
      type: "OPEN_EDIT",
      appType: key.appType,
      appTypeName: key.appTypeName,
      expiryDate: key.expiryDate ? dayjs(key.expiryDate).format("DD-MM-YYYY") : "",
    });
  };

  const handleTable = () => {
    navigate("/hptable");
  };

  return (
    <div className="api-key-list">
            {isLoading && (
        <div className="loader-container">
          <div className="loader">
            <Spinner />
          </div>
        </div>
      )}
      <div className="api-key-list_header">
        <div className="api-key-list_header_left">
          <div
            className="api-key-list_header_left_arrow"
            onClick={handleTable}
            onKeyDown={onKeyActivate(handleTable)}
            role="button"
            tabIndex={0}
          >
            <BackArrow onClick={handleTable} />
            <h3>App Keys</h3>
          </div>
        </div>
        <div className="api-key-list_header_right">
          <Buttons
            text="Add API Key"
            type="button"
            variant="primary"
            fullWidth={false}
            style={{ height: 40 }}
            onClick={handleGenerateKey}
            icon={<PlusIcon />}
          />
        </div>
      </div>
      <div className="table-container">
        <TableContainer component={Paper}>
          <Table aria-label="API keys table">
            <TableHead>
              <TableRow>
                <TableCell>SL NO</TableCell>
                <TableCell>App Type</TableCell>
                <TableCell>App Key</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appKeysData.length === 0 ? (
                <TableStatusRow
                  colSpan={API_KEY_TABLE_COLUMN_COUNT}
                  loading={isKeysLoading}
                  error={keysFetchError}
                  onRetry={getAppKeys}
                  emptyMessage="No API keys found"
                />
              ) : (
                appKeysData.map((key, index) => (
                  <TableRow key={key.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{key.appTypeName || "-"}</TableCell>
                    <TableCell>{key.appKey || "-"}</TableCell>
                    <TableCell>
                      {key.expiryDate
                        ? new Date(key.expiryDate).toLocaleDateString()
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <div
                        className="table_icons"
                        style={{ display: "flex", gap: "10px" }}
                      >
                        <CustomTooltip title="Edit App Key">
                          <IconButton
                            aria-label="edit"
                            onClick={() => handleAppKeyEdit(key)}
                          >
                            <EditIcons />
                          </IconButton>
                        </CustomTooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <FormModal
        open={keyForm.referenceModal}
        onClose={() => dispatchKeyForm({ type: "CLOSE" })}
        title={keyForm.isEditMode ? "Edit API Key" : "Generate API Key"}
        actions={
          <>
            <Buttons
              text="Close"
              type="button"
              variant="smallsecondary"
              onClick={() => dispatchKeyForm({ type: "CLOSE" })}
            />
            <Buttons
              text={keyForm.isEditMode ? "Update" : "Generate"}
              type="button"
              variant="smallprimary"
              onClick={hanldeAddApiKey}
            />
          </>
        }
      >
        <SelectInput
          title="Select App Type"
          name="appType"
          disabled={keyForm.isEditMode}
          value={keyForm.isEditMode ? keyForm.selectedAppTypeName : (keyForm.selectedAppType ?? "")}
          onChange={(e) => dispatchKeyForm({ type: "SET_APP_TYPE", value: e.target.value })}
          options={apiKeyList}
          placeholder="Select Device"
        />
        {keyForm.appTypeError && <span className="error">{keyForm.appTypeError}</span>}
        <DateInput
          title="Expiry Date"
          name="expiryDate"
          value={keyForm.expiryDate}
          onChange={(e) => dispatchKeyForm({ type: "SET_EXPIRY_DATE", value: e.target.value })}
          placeholder="DD-MM-YYYY"
          error={!!keyForm.expiryDateError}
          helperText={keyForm.expiryDateError}
        />
      </FormModal>
    </div>
  );
};

export default ApiKeyList;
